import {Observable} from 'rxjs';
import {CpmCOrganizationApiModel} from '../../models/c-customer/cpmc-organization.api-model';
import {
    CpmCOrganizationCreateApiSchema,
    CpmCOrganizationsNumbersApiSchema,
    CpmCOrganizationUpdateApiSchema
} from '../../schemas/c-customer/cpmc-organization.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetOrganizationsParams = {
    [K in keyof CpmCOrganizationApiModel]?: CpmCOrganizationApiModel[K] extends AllowedQueryParamTypes
        ? CpmCOrganizationApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * Organization Api Service
 */
export class CpmCOrganizationApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'name', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        return base + `customer/${customerUUID || inCustomerUUID}/organization`;
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCOrganizationApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCOrganizationApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, organizationUUID: string): string {
        return `${CpmCOrganizationApiService.baseUrl(inCustomerUUID)}/${organizationUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, organizationUUID: string): string {
        return `${CpmCOrganizationApiService.baseUrl(inCustomerUUID)}/${organizationUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, organizationUUID: string): string {
        return `${CpmCOrganizationApiService.baseUrl(inCustomerUUID)}/${organizationUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getOrganizationList(
        params: CpmCGetOrganizationsParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCOrganizationApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCOrganizationApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCOrganizationApiModel>>(
            CpmCOrganizationApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getOrganizationByUUID(organizationUUID: string): Observable<CpmCOrganizationApiModel> {
        return CpmCApiBridge.httpGet<CpmCOrganizationApiModel>(
            CpmCOrganizationApiService.endpointGet(undefined, organizationUUID)
        );
    }
    public static getOrganizationsNumbers(): Observable<CpmCOrganizationsNumbersApiSchema> {
        return CpmCApiBridge.httpGet<CpmCOrganizationsNumbersApiSchema>(
            `${CpmCOrganizationApiService.baseUrl(undefined)}/numbers`
        );
    }
    public static createOrganization(payload: CpmCOrganizationCreateApiSchema): Observable<CpmCOrganizationApiModel> {
        return CpmCApiBridge.httpPost<CpmCOrganizationApiModel>(
            CpmCOrganizationApiService.endpointNew(undefined),
            payload
        );
    }
    public static updateOrganization(
        organizationUUID: string,
        payload: CpmCOrganizationUpdateApiSchema
    ): Observable<CpmCOrganizationApiModel> {
        return CpmCApiBridge.httpPut<CpmCOrganizationApiModel>(
            CpmCOrganizationApiService.endpointEdit(undefined, organizationUUID),
            payload
        );
    }
    public static deleteOrganization(organizationUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCOrganizationApiService.endpointDelete(undefined, organizationUUID)
        );
    }
}
